import React, { useState } from "react";
import { Box, TextField, Button, Alert, Stack } from "@mui/material";
import { SERVER_BASE_URL } from "../Config";

interface ChangePasswordFormProps {
  tokenKey: "providerToken" | "pharmacistToken";
  onSuccess?: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ tokenKey, onSuccess }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "New password must be at least 6 characters" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem(tokenKey);
      const response = await fetch(`${SERVER_BASE_URL}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setMessage({ type: "error", text: text || `Server error (${response.status})` });
        return;
      }
      if (response.ok && data.success) {
        setMessage({ type: "success", text: "Password changed successfully" });
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        if (onSuccess) onSuccess();
      } else {
        setMessage({ type: "error", text: data.message || "Failed to change password" });
      }
    } catch (err: any) {
      setMessage({ type: "error", text: err?.message || "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, mx: "auto" }}>
      <Stack spacing={2}>
        {message && <Alert severity={message.type}>{message.text}</Alert>}
        <TextField
          label="Current Password"
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          fullWidth
          helperText="Minimum 6 characters"
        />
        <TextField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          fullWidth
          error={confirmPassword.length > 0 && newPassword !== confirmPassword}
          helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? "Passwords do not match" : ""}
        />
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? "Changing..." : "Change Password"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ChangePasswordForm;
